import { lazy, Suspense, type ReactElement } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { useAuthStore } from '@/store/auth.store';
import { Layout } from './components/Layout';
import { Spinner } from './components/fields';

import Login from './pages/Login';
import Signup from './pages/Signup';

// Страницы грузятся по мере открытия: кабинет чаще всего открывают с телефона,
// и первый экран не должен ждать графики аналитики и редакторы документов.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Finance = lazy(() => import('./pages/Finance'));
const Sales = lazy(() => import('./pages/Sales'));
const Returns = lazy(() => import('./pages/Returns'));
const Expenses = lazy(() => import('./pages/Expenses'));
const Items = lazy(() => import('./pages/Items'));
const Documents = lazy(() => import('./pages/Documents'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const Stock = lazy(() => import('./pages/Stock'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Profile = lazy(() => import('./pages/Profile'));
const PublicInvoice = lazy(() => import('./pages/PublicInvoice'));
const CouriersPage = lazy(() => import('./features/couriers/CouriersPage'));
const CourierDetail = lazy(() => import('./features/couriers/CourierDetail'));
const RouteEditor = lazy(() => import('./features/couriers/RouteEditor'));
const ChatScreen = lazy(() => import('./features/chat/ChatScreen'));
const StoresPage = lazy(() => import('./pages/Stores'));
const StaffPage = lazy(() => import('./pages/Staff'));
const StaffDetailPage = lazy(() => import('./pages/StaffDetail'));
const WorkPage = lazy(() => import('./pages/Work'));
const StoreRatingPage = lazy(() => import('./pages/StoreRating'));

function isWorkerHost() {
  return typeof window !== 'undefined' && window.location.hostname === 'worker.resstro.ru';
}

function ProtectedRoute({ children }: { children: ReactElement }) {
  const token = useAuthStore((s) => s.accessToken);
  const location = useLocation();
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

export default function App() {
  if (isWorkerHost()) {
    return (
      <Suspense fallback={<Spinner label="Загрузка…" />}>
        <WorkPage />
      </Suspense>
    );
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route
        path="/work"
        element={
          <Suspense fallback={<Spinner label="Загрузка…" />}>
            <WorkPage />
          </Suspense>
        }
      />
      {/* Публичная накладная по ссылке — открывается покупателем без входа. */}
      <Route
        path="/invoice/:token"
        element={
          <Suspense fallback={<Spinner label="Загрузка…" />}>
            <PublicInvoice />
          </Suspense>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<Spinner label="Загрузка…" />}>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/finance" element={<Finance />} />
                  <Route path="/sales" element={<Sales />} />
                  <Route path="/returns" element={<Returns />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/items" element={<Items />} />
                  <Route path="/documents" element={<Documents />} />
                  <Route path="/suppliers" element={<Suppliers />} />
                  <Route path="/stock" element={<Stock />} />
                  <Route path="/subscription" element={<Subscription />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/couriers" element={<CouriersPage />} />
                  <Route path="/stores" element={<StoresPage />} />
                  <Route path="/ratings" element={<StoreRatingPage />} />
                  <Route path="/staff" element={<StaffPage />} />
                  <Route path="/staff/:id" element={<StaffDetailPage />} />
                  <Route path="/couriers/routes/:id" element={<RouteEditor />} />
                  <Route path="/couriers/:id" element={<CourierDetail />} />
                  <Route path="/chat" element={<ChatScreen />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
